import clsx from 'clsx';
import {
  useClearRefinements,
  useInstantSearch,
  useRefinementList,
} from 'react-instantsearch';

import styles from './AlgoliaFilters.module.scss';

const PREPR_MODEL_PAGE_TYPES_COUNT = 20;
// Algolia model types grouped by category
const MODEL_GROUPS = {
  news: {
    label: 'Artikelen',
    models: ['Pagina: Artikel'],
  },
  events: {
    label: 'Evenementen',
    models: ['Pagina: Evenement'],
  },
  locations: {
    label: 'Locaties',
    models: ['Pagina: Locatie'],
  },
  routes: {
    label: 'Routes',
    models: ['Pagina: Route'],
  },
  activities: {
    label: 'Activiteiten',
    models: ['Pagina: Activiteit'],
  },
  content: {
    label: "Pagina's",
    models: [
      'Pagina: Content',
      'Pagina: Zigzag Campagne',
      'Pagina: Home',
      'Pagina: Landingspagina',
      'Pagina: Explore',
      'Pagina: Plattegrond',
      'Pagina: Locatie overzicht',
      'Pagina: Route overzicht',
      'Pagina: Artikel overzicht',
      'Pagina: Evenementen overzicht',
      'Hoofd categorie',
      'Sub categorie',
      'Sub-sub categorie',
    ],
  },
};

export default function AlgoliaFilters() {
  const { items } = useRefinementList({
    attribute: 'model',
    limit: PREPR_MODEL_PAGE_TYPES_COUNT,
    operator: 'or',
  });

  const { refine: clearRefine } = useClearRefinements({
    includedAttributes: ['model'],
  });

  const { setIndexUiState } = useInstantSearch();

  // Get currently selected group
  const getCurrentGroup = () => {
    const refinedModels = items
      .filter(item => item.isRefined)
      .map(item => item.value);

    return (
      Object.entries(MODEL_GROUPS).find(
        ([, group]) =>
          group.models.every(model => refinedModels.includes(model)) &&
          refinedModels.every(model => group.models.includes(model)),
      )?.[0] || ''
    );
  };

  const clearSelection = () => {
    clearRefine();
    setIndexUiState(state => ({
      ...state,
      refinementList: {
        ...state.refinementList,
        model: [],
      },
    }));
  };

  const handleGroupChange = (groupKey: keyof typeof MODEL_GROUPS) => {
    const currentGroup = getCurrentGroup();

    // If clicking the already selected radio button, clear the refinement
    if (currentGroup === groupKey) {
      clearSelection();
      return;
    }

    // Otherwise, apply the new refinement
    const { models } = MODEL_GROUPS[groupKey];
    clearRefine();
    setIndexUiState(state => ({
      ...state,
      refinementList: {
        ...state.refinementList,
        model: models,
      },
    }));
  };

  const getGroupCount = (groupModels: string[]) =>
    items
      .filter(item => groupModels.includes(item.value))
      .reduce((sum, item) => sum + item.count, 0);

  return (
    <ul className={clsx(styles.list, 'u-list-clean')}>
      {Object.entries(MODEL_GROUPS)
        .map(([key, group]) => ({
          key,
          group,
          count: getGroupCount(group.models),
          isCurrentGroup: getCurrentGroup() === key,
        }))
        .filter(({ count }) => count > 0)
        .sort((a, b) => b.count - a.count)
        .map(({ key, group, count: groupCount, isCurrentGroup }) => (
          <li className={styles.item} key={key}>
            <button
              type="button"
              className={styles.button}
              onClick={() =>
                handleGroupChange(key as keyof typeof MODEL_GROUPS)
              }
              aria-pressed={isCurrentGroup}
            >
              <input
                type="radio"
                name="modelGroup"
                value={key}
                checked={isCurrentGroup}
                aria-label={group.label}
                readOnly
                className={clsx(styles.input, 'u-visually-hidden')}
              />
              <span className={styles.label}>
                {group.label}
                <span
                  className={styles.count}
                  aria-label={`${groupCount} items`}
                >
                  {groupCount}
                </span>
              </span>
            </button>
          </li>
        ))}
    </ul>
  );
}
